import { Tabs } from 'components/V2/Tabs/Tabs';
import { useEffect, useState } from 'react';
import { CaretDown } from 'react-ionicons';
import Dropdown, { DropdownItem } from './Dropdown';
import { useSearchParams } from 'react-router-dom';
import { DeviceType } from '../../accessExpress.types';
import { parseDeviceParam } from 'screens/AccessExpressV3/utils';

enum Options {
  cellular = 'Cellular',
  network = 'Network',
  accessories = 'Accessories',
}

const LocalNav = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [selected, setSelected] = useState<
    'cellular' | 'network' | 'accessories'
  >('cellular');
  const [isOpen, setIsOpen] = useState(false);

  const onChangeDeviceType = (
    prevParams: URLSearchParams,
    value: DeviceType,
    extraParam?: string,
  ) => {
    const newParams = new URLSearchParams(prevParams);
    newParams.set(
      'type',
      encodeURIComponent(value + (extraParam ? `|${extraParam}` : '')),
    );
    return newParams;
  };

  const decodeType = parseDeviceParam(
    decodeURIComponent(searchParams.get('type') || ''),
  ).type as DeviceType;
  const decodeCategory = parseDeviceParam(
    decodeURIComponent(searchParams.get('type') || ''),
  ).category as string;

  const subOptions = {
    cellular: [
      {
        active: decodeType === DeviceType.smartPhone,
        onClick: () =>
          setSearchParams(
            (prevParams) =>
              onChangeDeviceType(prevParams, DeviceType.smartPhone),
            { replace: true },
          ),
        title: 'Smartphone',
        className: 'whitespace-nowrap',
      },
      {
        active: decodeType === DeviceType.sim,
        onClick: () =>
          setSearchParams(
            (prevParams) => onChangeDeviceType(prevParams, DeviceType.sim),
            { replace: true },
          ),
        title: 'SIM',
        className: 'whitespace-nowrap',
      },
      {
        active: decodeType === DeviceType.tablet,
        onClick: () =>
          setSearchParams(
            (prevParams) => onChangeDeviceType(prevParams, DeviceType.tablet),
            { replace: true },
          ),
        title: 'Tablet',
        className: 'whitespace-nowrap',
      },
      {
        active: decodeType === DeviceType.hotspot,
        onClick: () =>
          setSearchParams(
            (prevParams) => onChangeDeviceType(prevParams, DeviceType.hotspot),
            { replace: true },
          ),
        title: 'Hotspot',
        className: 'whitespace-nowrap',
      },
      {
        active: decodeType === DeviceType.basicPhone,
        onClick: () =>
          setSearchParams(
            (prevParams) =>
              onChangeDeviceType(prevParams, DeviceType.basicPhone),
            { replace: true },
          ),
        title: 'Basic phone',
        className: 'whitespace-nowrap',
      },
    ],
    network: [
      {
        active: decodeType === DeviceType.wirelessRouter,
        onClick: () =>
          setSearchParams(
            (prevParams) =>
              onChangeDeviceType(prevParams, DeviceType.wirelessRouter),
            { replace: true },
          ),
        title: 'Wireless router',
        className: 'whitespace-nowrap',
      },
      {
        active: decodeType === DeviceType.modem,
        onClick: () =>
          setSearchParams(
            (prevParams) => onChangeDeviceType(prevParams, DeviceType.modem),
            { replace: true },
          ),
        title: 'Modem / IoT',
        className: 'whitespace-nowrap',
      },
    ],
    accessories: [
      {
        active: decodeType === DeviceType.universal,
        onClick: () =>
          setSearchParams(
            (prevParams) =>
              onChangeDeviceType(prevParams, DeviceType.universal, 'ACCESSORY'),
            { replace: true },
          ),
        title: 'Universal',
        className: 'whitespace-nowrap',
      },
      {
        active: decodeType === DeviceType.wirelessRouter,
        onClick: () =>
          setSearchParams(
            (prevParams) =>
              onChangeDeviceType(
                prevParams,
                DeviceType.wirelessRouter,
                'ACCESSORY',
              ),
            { replace: true },
          ),
        title: 'Wireless router',
        className: 'whitespace-nowrap',
      },
      {
        active: decodeType === DeviceType.smartPhone,
        onClick: () =>
          setSearchParams(
            (prevParams) =>
              onChangeDeviceType(
                prevParams,
                DeviceType.smartPhone,
                'ACCESSORY',
              ),
            { replace: true },
          ),
        title: 'Smart phone',
        className: 'whitespace-nowrap',
      },
      {
        active: decodeType === DeviceType.tablet,
        onClick: () =>
          setSearchParams(
            (prevParams) =>
              onChangeDeviceType(prevParams, DeviceType.tablet, 'ACCESSORY'),
            { replace: true },
          ),
        title: 'Tablet',
        className: 'whitespace-nowrap',
      },
    ],
  };

  useEffect(() => {
    if (decodeCategory === 'ACCESSORY') {
      setSelected('accessories');
    } else if (
      decodeCategory !== 'ACCESSORY' &&
      (decodeType === DeviceType.wirelessRouter ||
        decodeType === DeviceType.modem)
    ) {
      setSelected('network');
    } else {
      setSelected('cellular');
    }
  }, [decodeCategory, decodeType]);

  return (
    <div className="flex w-full flex-col flex-wrap gap-4 md:h-8 md:flex-row md:items-center">
      <Dropdown
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        triggerClassName="w-[170px] justify-start whitespace-nowrap text-content-base-default"
        label={
          <>
            {Options[selected]}
            <CaretDown width="20px" height="20px" color="currentColor" />
          </>
        }
      >
        <DropdownItem
          active={selected === 'network'}
          label="Network"
          onClick={() => {
            setSelected('network');
            setSearchParams(
              (prevParams) =>
                onChangeDeviceType(prevParams, DeviceType.wirelessRouter),
              { replace: true },
            );
            setIsOpen(false);
          }}
        />
        <DropdownItem
          active={selected === 'cellular'}
          label="Cellular"
          onClick={() => {
            setSelected('cellular');
            setSearchParams(
              (prevParams) =>
                onChangeDeviceType(prevParams, DeviceType.smartPhone),
              { replace: true },
            );
            setIsOpen(false);
          }}
        />
        <DropdownItem
          active={selected === 'accessories'}
          label="Accessories"
          onClick={() => {
            setSelected('accessories');
            setSearchParams(
              (prevParams) =>
                onChangeDeviceType(
                  prevParams,
                  DeviceType.universal,
                  'ACCESSORY',
                ),
              { replace: true },
            );
            setIsOpen(false);
          }}
        />
      </Dropdown>

      <div className="h-1 w-full border-b border-stroke-base-subdued md:h-8 md:w-0 md:border-r" />
      <Tabs
        variant="pill-accent-primary"
        className="flex-1 flex-wrap gap-4 overflow-x-auto p-0.5 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-stroke-base-subdued md:flex-nowrap"
        size="medium"
        tabs={subOptions[selected]}
        withBorder={false}
      />
    </div>
  );
};

export default LocalNav;
